import {
  $apiClient,
  $lang,
  $translationCtx,
  isEquivalentSection,
  SectionByMicroservice,
  setValueToLocalStorage,
  Translation,
} from '@msdk/common'
import { LoadedSection } from '@msdk/definitions'
import { RecoilValueReadOnly, selector } from 'recoil'

import { SectionData } from './section-data'

export const I18N_LOCAL_STORAGE_KEY = 'TRANSLATIONS'

export interface TransaltionContext {
  /** Locale requested when the translation load started, for example `en-US`. */
  locale: string
  /** Translation-content version requested when the load started. */
  version: string
  /** Sections returned for this locale and version. */
  sections: SectionData[]
}

export const $i18Version: RecoilValueReadOnly<string> = selector({
  key: 'i18Version',
  get: async ({ get }) => {
    const client = get($apiClient)
    const lang = get($lang)
    const res = await client.i18n.getVersion(lang)
    return res
  },
})

export const $i18nUpdater = selector<any>({
  key: 'i18nUpdater',
  get: () => {
    throw new Error('Not implemented')
  },
  set: ({ get, set }, translationContext: TransaltionContext) => {
    const currentCtx = get($translationCtx)
    const currentLocale = get($lang)
    const currentVersion = get($i18Version)

    // Translation requests can finish out of order. Ignore this result if the user has selected
    // another locale or a newer translation version became active while the request was running.
    if (translationContext.locale !== currentLocale || translationContext.version !== currentVersion) {
      return
    }

    // Reuse existing translations only for another section of the same locale and version.
    // Otherwise start with empty data so values and loaded-section markers are never mixed across
    // languages or translation versions.
    let newCtx =
      currentCtx.currentLocale === translationContext.locale && currentCtx.version === translationContext.version
        ? currentCtx
        : {
            currentLocale: translationContext.locale,
            data: {},
            loadedSections: [],
            version: translationContext.version,
          }

    translationContext.sections.forEach((s) => {
      newCtx = mergeCtx(newCtx, s.loadedSection, s.sectionInfo, translationContext.version, translationContext.locale)
    })
    set($translationCtx, newCtx)
    setValueToLocalStorage({ key: I18N_LOCAL_STORAGE_KEY, value: newCtx })
  },
})

const mergeCtx = (
  oldCtx: Translation,
  loadedSection: LoadedSection,
  sectionInfo: SectionByMicroservice,
  version: string,
  locale: string,
): Translation => {
  const ctx: Translation = {
    currentLocale: locale,
    data: { ...oldCtx.data },
    loadedSections: [...oldCtx.loadedSections],
    version,
  }

  if (!ctx.loadedSections.some((section) => isEquivalentSection(section, sectionInfo))) {
    ctx.loadedSections.push(sectionInfo)
  }

  //There might not be any translation for that ms
  if (loadedSection.idmLs) {
    const newIDMLs: Record<string, { msName: string; sectionName: string; tDescription: string; isSystem: boolean }> =
      {}
    loadedSection.idmLs.forEach(
      (entry) =>
        (newIDMLs[entry.idml] = {
          msName: sectionInfo.msName,
          sectionName: sectionInfo.sectionName,
          tDescription: entry.tDescription,
          isSystem: entry.isSystem,
        }),
    )

    const updatedData = { ...ctx.data }
    Object.keys(newIDMLs).forEach((key) => {
      if (!updatedData[key]) {
        updatedData[key] = newIDMLs[key]
      }
    })

    ctx.data = updatedData
  }

  return ctx
}
